import { useState } from "react";
import { ChartLineUp, Files, Money, Receipt, type Icon } from "@phosphor-icons/react";
import { DateRangePicker, type DateRangeValue } from "@/components/admin/DateRangePicker";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { defaultRange } from "./report-shared";
import { RevenueReportBody } from "./RevenueReportBody";
import { PayoutsReportBody } from "./PayoutsReportBody";
import { SubscriptionsReportBody } from "./SubscriptionsReportBody";
import { OperationsReportBody } from "./OperationsReportBody";

type ReportId = "revenue" | "payouts" | "subscriptions" | "operations";

interface ReportMeta {
  id: ReportId;
  title: string;
  cardDescription: string;
  bodyDescription: string;
  icon: Icon;
}

const REPORTS: ReportMeta[] = [
  {
    id: "revenue",
    title: "Izvještaj o prihodima",
    cardDescription: "Prihod, platform fee i dnevni trend po odabranom periodu.",
    bodyDescription: "Ukupni prihod i platform fee iz završenih uplata u odabranom periodu.",
    icon: ChartLineUp,
  },
  {
    id: "payouts",
    title: "Isplate",
    cardDescription: "Pregled obaveza prema terapeutima.",
    bodyDescription: "Isplate terapeutima u periodu i trenutno neisplaćeni iznos.",
    icon: Money,
  },
  {
    id: "subscriptions",
    title: "Pretplate",
    cardDescription: "Mjesečni i godišnji prihodi od pretplata.",
    bodyDescription: "Bruto, neto i PDV iz naplaćenih pretplata u odabranom periodu.",
    icon: Receipt,
  },
  {
    id: "operations",
    title: "Operacije",
    cardDescription: "Volumen sesija, klijenata i terapeuta.",
    bodyDescription: "Volumen sesija i broj aktivnih korisnika u odabranom periodu.",
    icon: Files,
  },
];

export function ReportsPage() {
  const [selected, setSelected] = useState<ReportId>("revenue");
  const [dateRange, setDateRange] = useState<DateRangeValue>(defaultRange);
  const active = REPORTS.find((r) => r.id === selected) ?? REPORTS[0]!;

  return (
    <div className="flex flex-col gap-5">
      <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {REPORTS.map((report) => {
          const Icon = report.icon;
          const isActive = report.id === selected;
          return (
            <button
              key={report.id}
              type="button"
              onClick={() => setSelected(report.id)}
              aria-pressed={isActive}
              className="rounded-xl text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            >
              <Card
                className={cn(
                  "h-full gap-4 py-4 transition-colors",
                  isActive
                    ? "border-primary/30 bg-primary/10"
                    : "border-border/60 opacity-75 hover:opacity-100",
                )}
              >
                <CardHeader className="gap-3 px-4">
                  <CardAction>
                    <span
                      className={cn(
                        "flex size-9 items-center justify-center rounded-lg",
                        isActive ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground",
                      )}
                    >
                      <Icon weight="duotone" />
                    </span>
                  </CardAction>
                  <CardTitle className="pr-10 text-base">{report.title}</CardTitle>
                  <CardDescription>{report.cardDescription}</CardDescription>
                </CardHeader>
              </Card>
            </button>
          );
        })}
      </section>

      <Card className="border-border/60">
        <CardHeader className="gap-4 md:flex md:flex-row md:items-end md:justify-between">
          <div>
            <CardTitle className="text-2xl" style={{ fontFamily: "var(--font-heading)" }}>
              {active.title}
            </CardTitle>
            <CardDescription>{active.bodyDescription}</CardDescription>
          </div>
          <CardAction className="static col-auto row-auto self-auto justify-self-auto">
            <DateRangePicker value={dateRange} onChange={setDateRange} />
          </CardAction>
        </CardHeader>
        <CardContent className="flex flex-col gap-5">
          {selected === "revenue" && <RevenueReportBody dateRange={dateRange} />}
          {selected === "payouts" && <PayoutsReportBody dateRange={dateRange} />}
          {selected === "subscriptions" && <SubscriptionsReportBody dateRange={dateRange} />}
          {selected === "operations" && <OperationsReportBody dateRange={dateRange} />}
        </CardContent>
      </Card>
    </div>
  );
}
